import { useState, useEffect } from 'react'
import { format } from 'date-fns'
import { useAuth } from '../../../hooks/useAuth'
import { useLinkedHouseholds } from '../../linkedHouseholds/hooks/useLinkedHouseholds'
import { useSharedMembers } from '../../linkedHouseholds/hooks/useSharedMembers'
import { useCustodyExpenses } from '../hooks/useCustody'
import { Spinner } from '../../../components/ui/Spinner'
import { ConflictModal } from '../../../components/ConflictModal'
import { supabase } from '../../../lib/supabase'
import { cn } from '../../../lib/utils'
import { Portal } from '../../../components/Portal'

const CATEGORIES = [
  { id: 'medical',   label: 'Medical' },
  { id: 'dental',    label: 'Dental' },
  { id: 'school',    label: 'School' },
  { id: 'activities', label: 'Activities' },
  { id: 'clothing',  label: 'Clothing' },
  { id: 'childcare', label: 'Childcare' },
  { id: 'travel',    label: 'Travel' },
  { id: 'other',     label: 'Other' },
]

export const AddExpenseForm = ({ expense, onClose }) => {
  const { household, user }         = useAuth()
  const { activeLink }              = useLinkedHouseholds()
  const { sharedOut, sharedIn }     = useSharedMembers()
  const { create, update }          = useCustodyExpenses()

  const linkedHouseholdName = activeLink?.otherHousehold?.name ?? 'Other House'
  const allShared = [...sharedOut, ...sharedIn]

  const [childId,     setChildId]     = useState(expense?.child_id     ?? allShared[0]?.user_id ?? '')
  const [category,    setCategory]    = useState(expense?.category     ?? 'other')
  const [description, setDescription] = useState(expense?.description  ?? '')
  const [amount,      setAmount]      = useState(expense?.amount       ?? '')
  const [date,        setDate]        = useState(expense?.expense_date ?? format(new Date(), 'yyyy-MM-dd'))
  const [splitType,   setSplitType]   = useState(expense?.split_type   ?? 'fifty_fifty')
  const [splitPct,    setSplitPct]    = useState(expense?.split_percentage ?? 50)
  const [notes,       setNotes]       = useState(expense?.notes        ?? '')
  const [receiptFile, setReceiptFile] = useState(null)
  const [uploading,   setUploading]   = useState(false)
  const [saving,      setSaving]      = useState(false)
  const [error,       setError]       = useState('')
  const [conflict,    setConflict]    = useState(null)
  const [pendingPayload, setPendingPayload] = useState(null)

  const amtNum      = parseFloat(amount) || 0
  const otherOwes   = splitType === 'fifty_fifty' ? amtNum * 0.5 : amtNum * (splitPct / 100)
  const youKeep     = amtNum - otherOwes

  const handleSave = async () => {
    if (!childId)          { setError('Select a child.'); return }
    if (!description.trim()) { setError('Enter a description.'); return }
    if (!amtNum || amtNum <= 0) { setError('Enter a valid amount.'); return }
    setSaving(true)
    setError('')
    try {
      let receiptPath = expense?.receipt_path ?? null
      if (receiptFile) {
        setUploading(true)
        const ext  = receiptFile.name.split('.').pop()
        const path = `${household.id}/custody/receipts/${crypto.randomUUID()}.${ext}`
        const { error: upErr } = await supabase.storage
          .from('household-documents')
          .upload(path, receiptFile, { upsert: true })
        if (upErr) throw upErr
        receiptPath = path
        setUploading(false)
      }

      const payload = {
        child_id:         childId,
        category,
        description:      description.trim(),
        amount:           amtNum,
        expense_date:     date,
        split_type:       splitType,
        split_percentage: splitType === 'custom' ? splitPct : null,
        notes:            notes.trim() || null,
        receipt_path:     receiptPath,
      }

      if (expense) {
        const result = await update.mutateAsync({
          id: expense.id,
          localUpdatedAt: expense.updated_at,
          ...payload,
        })
        if (result?.conflict) {
          setPendingPayload(payload)
          setConflict(result.conflict)
          setSaving(false)
          return
        }
      } else {
        await create.mutateAsync(payload)
      }
      onClose()
    } catch (err) {
      setError(err.message ?? 'Save failed.')
      setSaving(false)
      setUploading(false)
    }
  }

  const handleKeepMine = async () => {
    setSaving(true)
    setConflict(null)
    try {
      await update.mutateAsync({ id: expense.id, ...pendingPayload })
      onClose()
    } catch (err) {
      setError(err.message ?? 'Save failed.')
      setSaving(false)
    }
  }

  const inputCls = 'w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30'
  const lbl = (t) => <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">{t}</label>

  return (
    <Portal>
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col shadow-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border flex-shrink-0">
          <h2 className="text-base font-bold text-text-primary">
            {expense ? 'Edit Expense' : 'Add Expense'}
          </h2>
          <button type="button" onClick={onClose} className="text-text-muted p-1">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4 space-y-4">
          {/* Child */}
          <div>
            {lbl('Child *')}
            <div className="flex flex-wrap gap-2">
              {allShared.map((sm) => (
                <button
                  key={sm.user_id}
                  type="button"
                  onClick={() => setChildId(sm.user_id)}
                  className={cn(
                    'px-4 py-1.5 rounded-full text-sm font-medium border transition-colors',
                    childId === sm.user_id ? 'bg-primary text-white border-primary' : 'border-border text-text-secondary',
                  )}
                >
                  {sm.profiles?.display_name}
                </button>
              ))}
            </div>
          </div>

          {/* Category */}
          <div>
            {lbl('Category')}
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCategory(c.id)}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-sm font-medium border transition-colors',
                    category === c.id ? 'bg-primary text-white border-primary' : 'border-border text-text-secondary',
                  )}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Description + Amount */}
          <div>
            {lbl('Description *')}
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Annual checkup at Dr. Lee"
              className={inputCls}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              {lbl('Amount *')}
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                min="0"
                step="0.01"
                className={inputCls}
              />
            </div>
            <div>
              {lbl('Date')}
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
            </div>
          </div>

          {/* Split */}
          <div>
            {lbl('Split')}
            <div className="flex gap-2 mb-3">
              {[
                { id: 'fifty_fifty', label: '50 / 50' },
                { id: 'custom',      label: 'Custom' },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setSplitType(id)}
                  className={cn(
                    'flex-1 py-2.5 rounded-xl border text-sm font-medium transition-colors',
                    splitType === id ? 'bg-primary text-white border-primary' : 'border-border text-text-secondary',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            {splitType === 'custom' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-muted">
                    {linkedHouseholdName} owes {splitPct}%
                  </span>
                  <span className="text-xs font-semibold text-text-primary">
                    ${(amtNum * splitPct / 100).toFixed(2)}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={splitPct}
                  onChange={(e) => setSplitPct(parseInt(e.target.value))}
                  className="w-full accent-primary"
                />
              </div>
            )}
            {amtNum > 0 && (
              <div className="mt-2 bg-background rounded-xl px-4 py-3 text-xs text-text-secondary space-y-1">
                <div className="flex justify-between">
                  <span>{linkedHouseholdName} owes</span>
                  <span className="font-semibold text-text-primary">${otherOwes.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>You keep</span>
                  <span className="font-semibold text-text-primary">${youKeep.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Receipt */}
          <div>
            {lbl('Receipt')}
            <label className="flex items-center gap-3 cursor-pointer border border-dashed border-border rounded-xl px-4 py-3 hover:border-primary transition-colors">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-text-muted">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
              </svg>
              <span className="text-sm text-text-secondary">
                {receiptFile ? receiptFile.name : expense?.receipt_path ? 'Receipt uploaded' : 'Upload receipt'}
              </span>
              <input
                type="file"
                accept="image/*,.pdf"
                className="hidden"
                onChange={(e) => setReceiptFile(e.target.files?.[0] ?? null)}
              />
            </label>
          </div>

          {/* Notes */}
          <div>
            {lbl('Notes')}
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes…"
              className={inputCls}
            />
          </div>

          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>

        <div className="px-5 py-4 border-t border-border flex gap-2 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="flex-1 border border-border rounded-xl py-3 text-sm font-medium text-text-secondary">
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={saving || uploading}
            className="flex-1 bg-primary text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
            {(saving || uploading) ? <><Spinner size="sm" /> {uploading ? 'Uploading…' : 'Saving…'}</> : 'Save Expense'}
          </button>
        </div>
      </div>

      {conflict && (
        <ConflictModal
          localRecord={pendingPayload}
          serverRecord={conflict.serverRecord}
          onKeepMine={handleKeepMine}
          onUseServer={() => { setConflict(null); onClose() }}
        />
      )}
    </div>
    </Portal>
  )
}
